import { forwardRef, type CSSProperties, type HTMLAttributes } from 'react';
import { Link } from '@tanstack/react-router';
import { Calendar, Clock, MoreHorizontal } from 'lucide-react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { SubtaskCountBadge } from '@/components/SubtaskCountBadge';
import { formatDue, type DueProximity } from '@/lib/dates';
import { cn } from '@/lib/utils';
import type { Priority } from '@/components/glass';

export interface TaskCardData {
  id: string;
  title: string;
  /** Server-computed `{prefix}-{sequence}` (slice 10) — rendered as
   *  the Linear-style monospace prefix above the title. */
  code: string | null;
  /** Status ULID — used by `BoardSwimlane` to group cards under
   *  status-name dividers within a swimlane. */
  statusId: string | null;
  statusColor: string;
  statusName: string;
  isComplete: boolean;
  /** Glass redesign — Phase 1 backend addition. */
  priority: Priority;
  /** Glass redesign — Phase 1 backend addition. Minutes; null if not set. */
  estimateMinutes: number | null;
  /** Sum of the task's logged time entries. Optional — controllers
   *  that don't loadSum leave this null and the card hides the chip. */
  loggedMinutes: number | null;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  dueAt: string | null;
  tagIds: string[];
  subtaskCount: number;
}

export interface TagLookup {
  get: (id: string) => { name: string; color: string } | undefined;
}

export interface TaskCardProps extends HTMLAttributes<HTMLDivElement> {
  task: TaskCardData;
  workspaceSlug: string;
  tagLookup: TagLookup;
  /** When true, renders styling for the dnd drag overlay (lift + tilt). */
  isDragOverlay?: boolean;
  /** When true, the source card while it's being dragged — fade & no link. */
  isDragGhost?: boolean;
  /** Optional inline style passthrough for sortable transforms. */
  style?: CSSProperties;
}

/**
 * Glass redesign — Kanban card.
 *
 * Mirrors the prototype's `.v1-card` exactly: padding, padded-out
 * status rail (top:14px / bottom:14px), card head (code + status
 * dot + status name + hover-revealed `more`), title, tag chips,
 * footer with `border-top` divider (due pill + time `0h/4h` with
 * /estimate muted + ringed avatar).
 *
 * Drag overlay state: rotate(1.5deg) + scale(1.02) + brand-orange
 * 1px ring + 24px-spread heavy shadow.
 * Ghost state: opacity 0.35.
 */
export const TaskCard = forwardRef<HTMLDivElement, TaskCardProps>(function TaskCard(
  { task, workspaceSlug, tagLookup, isDragOverlay = false, isDragGhost = false, style, className, ...rest },
  ref,
) {
  const due = formatDue(task.dueAt);
  const tags = task.tagIds
    .slice(0, 3)
    .map((id) => ({ id, def: tagLookup.get(id) }))
    .filter((t): t is { id: string; def: { name: string; color: string } } => Boolean(t.def));

  return (
    <article
      ref={ref}
      style={style}
      className={cn(
        // Prototype: .v1-card
        'group/card relative cursor-grab select-none rounded-xl border backdrop-blur-md',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
        // Specific padding from .v1-card { padding: 12px 14px 12px 18px }
        'pt-3 pr-3.5 pb-3 pl-[18px]',
        // Hover: lift + heavier shadow + brand-violet border
        'transition-[transform,box-shadow,border-color] duration-150 ease-out',
        'hover:-translate-y-px hover:border-[var(--glass-card-hover-border)]',
        'active:cursor-grabbing',
        // Default + hover shadow via inline arbitrary values
        'shadow-[var(--glass-card-shadow)] hover:shadow-[var(--glass-card-hover-shadow)]',
        // Focus ring
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-accent)]',
        // Drag-overlay (lifted card chasing the cursor)
        isDragOverlay && [
          'rotate-[1.5deg] scale-[1.02] cursor-grabbing border-[var(--brand-accent)]',
          'shadow-[0_24px_60px_oklch(0_0_0/0.25)]',
        ],
        // Ghost (source while dragging)
        isDragGhost && 'pointer-events-none opacity-[0.35]',
        className,
      )}
      tabIndex={0}
      {...rest}
    >
      {/*
        Status rail — absolutely positioned 2px stripe, inset 14px
        top + bottom (matches prototype `.v1-card-rail`). Opacity
        0.5. Rounded on the right edge so it reads as a pill emerging
        from the card's left side, not a hard line.
      */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-[14px] bottom-[14px] w-[2px] rounded-r-full opacity-50"
        style={{ background: task.statusColor }}
      />

      {/* Card head: code · status dot+name · spacer · more (hover) */}
      <div className="mb-1.5 flex items-center gap-2">
        {task.code ? (
          <span className="text-foreground-muted shrink-0 font-mono text-[10.5px] tracking-[0.02em] whitespace-nowrap">
            {task.code}
          </span>
        ) : null}
        <span className="text-foreground-muted ml-0.5 inline-flex shrink-0 items-center gap-[5px] text-[10.5px] whitespace-nowrap">
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: task.statusColor }}
          />
          {task.statusName}
        </span>
        <button
          type="button"
          aria-label="More"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            'text-foreground-muted ml-auto inline-flex h-[22px] w-[22px] items-center justify-center rounded-md',
            'opacity-0 transition-opacity duration-100',
            'group-hover/card:opacity-100',
            'hover:bg-[oklch(0_0_0/0.05)] hover:text-foreground',
            'dark:hover:bg-[oklch(1_0_0/0.06)]',
          )}
        >
          <MoreHorizontal className="size-3.5" />
        </button>
      </div>

      {/* Title — `<h3>` per prototype, but wrapped in the navigation
          link. Stop pointerdown so drag doesn't swallow the click. */}
      <h3 className="mb-2">
        <Link
          to="/w/$workspaceSlug/t/$taskUlid"
          params={{ workspaceSlug, taskUlid: task.id }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'block text-[13.5px] font-medium leading-[1.4] tracking-[-0.01em] text-pretty transition-colors',
            'hover:text-foreground',
            task.isComplete ? 'text-foreground-muted line-through' : 'text-foreground',
          )}
        >
          {task.title}
        </Link>
      </h3>

      {/* Tag chips — neutral grey background + colored 6×6 dot, NOT
          tinted by the tag's color. Limit 3 + "+N" overflow indicator. */}
      {tags.length > 0 ? (
        <div className="mb-2.5 flex flex-wrap items-center gap-1">
          {tags.map((t) => (
            <span
              key={t.id}
              className="text-foreground-muted inline-flex items-center gap-1 rounded-full bg-[var(--surface-quiet)] px-[7px] py-px text-[10.5px] font-medium"
            >
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: t.def.color }}
              />
              {t.def.name}
            </span>
          ))}
          {task.tagIds.length > 3 ? (
            <span className="text-foreground-faint text-[10.5px]">
              +{task.tagIds.length - 3}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Footer — `border-top` divider per prototype. Left: due + time.
          Right: ringed assignee avatar. */}
      <footer className="flex items-center justify-between border-t border-[var(--glass-card-border)] pt-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {due ? <DuePill due={due} /> : null}
          {/* Time: "0h/4h" with /4h muted. Prototype always shows it
              because every prototype task has both fields; we hide
              the whole chip when neither logged nor estimate is set. */}
          {(task.loggedMinutes !== null || task.estimateMinutes !== null) ? (
            <span className="text-foreground-muted inline-flex items-center gap-1 font-mono text-[11px] whitespace-nowrap">
              <Clock className="size-3" aria-hidden="true" />
              <span>{minutesToShort(task.loggedMinutes ?? 0)}</span>
              {task.estimateMinutes !== null ? (
                <span className="text-foreground-faint">/{minutesToShort(task.estimateMinutes)}</span>
              ) : null}
            </span>
          ) : null}
          <SubtaskCountBadge count={task.subtaskCount} />
        </div>
        <Avatar22
          assigneeId={task.assigneeId}
          assigneeName={task.assigneeName}
          assigneeEmail={task.assigneeEmail}
        />
      </footer>
    </article>
  );
});

/**
 * 22px ringed avatar — matches the prototype's `Avatar size={22} ring`.
 * MemberAvatar's `size` enum doesn't include 22 specifically (closest
 * is `sm` at 24); we override via inline width/height + ring style
 * so the dimensions match the prototype precisely without expanding
 * the MemberAvatar API.
 *
 * The 2px ring uses `--background` so the avatar reads as cleanly
 * separated from whatever surface sits behind it.
 */
const Avatar22 = ({
  assigneeId,
  assigneeName,
  assigneeEmail,
}: {
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
}) => {
  if (!assigneeId) {
    return (
      <span
        aria-hidden="true"
        className="text-foreground-faint inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[var(--surface-quiet)] text-[9px] font-semibold"
      >
        ?
      </span>
    );
  }
  return (
    <MemberAvatar
      id={assigneeId}
      name={assigneeName}
      email={assigneeEmail}
      size="xs"
      className="!h-[22px] !w-[22px] !text-[9px] shadow-[0_0_0_2px_var(--background)]"
    />
  );
};

const DUE_TINTS: Record<DueProximity, string> = {
  // Match the prototype's exact tints — these are NOT just colour-mix
  // versions of the status colours; they're hand-picked at specific
  // alpha + lightness so the badge reads as soft, not loud.
  overdue:
    'bg-[oklch(0.65_0.2_25/0.15)] text-[oklch(0.5_0.2_25)] dark:text-[oklch(0.78_0.18_25)]',
  today:
    'bg-[oklch(0.74_0.16_50/0.18)] text-[oklch(0.45_0.16_50)] dark:text-[oklch(0.85_0.16_50)]',
  tomorrow: 'bg-[var(--surface-quiet)] text-foreground-muted',
  'this-week': 'bg-[var(--surface-quiet)] text-foreground-muted',
  later: 'bg-[var(--surface-quiet)] text-foreground-muted',
  past:
    'bg-[oklch(0.65_0.2_25/0.15)] text-[oklch(0.5_0.2_25)] dark:text-[oklch(0.78_0.18_25)]',
};

const DuePill = ({ due }: { due: { label: string; proximity: DueProximity } }) => (
  <span
    className={cn(
      // Square-ish 5px radius per prototype, NOT round.
      'inline-flex items-center gap-1 rounded-[5px] px-1.5 py-px text-[11px] font-medium whitespace-nowrap',
      DUE_TINTS[due.proximity],
    )}
  >
    <Calendar className="size-3" aria-hidden="true" />
    {due.label}
  </span>
);

/**
 * Render minutes in the prototype's compact form — matches the seed
 * data style: "0h", "45m", "1h 30m", "3h 20m", "8h".
 *
 * Different from `formatTaskTime` in lib/format-task: that one was
 * a Phase 2 helper that I'll leave for later phases. This local copy
 * keeps card formatting pixel-locked to the prototype's display.
 */
function minutesToShort(min: number): string {
  if (min === 0) return '0h';
  const safe = Math.max(0, Math.floor(min));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
